import React, { useMemo } from "react";
import { Polyline } from "react-leaflet";

import MapConfig from "services/map/MapConfig";
import MapPopup from "components/MapPopup";

type Props = {
  hover: boolean;
  color: string;
  weight: number;
  zoom: number;
  positions: [];
  options: {
    meta_data: { area: { type: string } };
    type: string;
    area_type: string;
    size: string;
    arrival_time: string;
    duration: string;
    leaving_time: string;
    total_duration: string;
    available_from: string;
    available_to: string;
    timestamp: string;
    fill_color: { [key: string]: string };
    border_color: { [key: string]: string };
    border_weight: { [key: string]: number };
  };
  selected: boolean;
};

function MapAvailableParkingLine({
  hover,
  options,
  color,
  selected,
  weight,
  zoom,
  positions,
}: Props): JSX.Element {
  const markerType = useMemo(() => {
    if (selected) {
      return "selected";
    }
    if (hover) {
      return "hover";
    }

    return options?.meta_data?.area?.type || "normal";
  }, [hover, selected, options]);

  const stroke = useMemo(() => {
    const defaultWeight = MapConfig?.polyline?.stroke?.default || 7; // default weight
    const weightFromConfig =
      MapConfig?.polyline?.stroke?.[`zoom${zoom}`] || defaultWeight;
    const lineWeight = options?.border_weight?.[markerType] || weightFromConfig;

    return weight || lineWeight;
  }, [options, markerType, weight, zoom]);

  const computedColor = useMemo(() => {
    const markerType = options?.type || "default"; // probably_car ; car ; spot ; area

    const defaultFillColor = MapConfig?.marker?.fillColor?.default || "#EB1547";
    const markerFillColorFromConfig =
      MapConfig?.marker?.fillColor?.[markerType] || defaultFillColor;
    const markerFillColor =
      options?.fill_color?.[markerType] || markerFillColorFromConfig;

    return color || markerFillColor;
  }, [color, options]);

  return (
    <Polyline
      positions={positions}
      opacity={1}
      fill
      weight={stroke}
      color={computedColor}
    >
      <MapPopup
        // @ts-ignore
        areaType={options?.area_type}
        size={options?.size}
        arrivalTime={options?.arrival_time}
        duration={options?.duration}
        leavingTime={options?.leaving_time}
        totalDuration={options?.total_duration}
        availableFrom={options?.available_from}
        availableTo={options?.available_to}
        timestamp={options?.timestamp}
        type={options?.meta_data?.area.type}
      />
    </Polyline>
  );
}

export default MapAvailableParkingLine;
